import { useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useSession } from '../../lib/auth/session.js';
import { logout } from '../../lib/api/client.js';
import { TenantSwitcher } from './TenantSwitcher.js';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Hidden when the user lacks this. A usability affordance, not a control. */
  permission?: string;
  /** Platform/reseller only. */
  scopes?: Array<'platform' | 'reseller' | 'tenant' | 'site'>;
}

interface NavSection { title: string; items: NavItem[] }

const NAV: NavSection[] = [
  {
    title: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: '◧' },
      { to: '/calls', label: 'Call history', icon: '☎', permission: 'cdr:read' },
    ],
  },
  {
    title: 'Telephony',
    items: [
      { to: '/extensions', label: 'Extensions', icon: '⌗', permission: 'extension:read' },
      { to: '/numbers', label: 'Numbers', icon: '#', permission: 'number:read' },
      { to: '/flows', label: 'Call flows', icon: '⑃', permission: 'flow:read' },
      { to: '/queues', label: 'Queues', icon: '⋮', permission: 'queue:read' },
    ],
  },
  {
    title: 'Platform',
    items: [
      { to: '/tenants', label: 'Tenants', icon: '⬢', permission: 'tenant:read', scopes: ['platform', 'reseller'] },
      { to: '/carriers', label: 'Carriers', icon: '⇄', permission: 'carrier:read', scopes: ['platform'] },
      { to: '/nodes', label: 'Nodes', icon: '▣', permission: 'node:read', scopes: ['platform'] },
    ],
  },
  {
    title: 'Account',
    items: [
      { to: '/billing', label: 'Billing', icon: '⛁', permission: 'billing:read' },
      { to: '/settings', label: 'Settings', icon: '⚙' },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const session = useSession((s) => s.session);
  const can = useSession((s) => s.can);
  const clear = useSession((s) => s.clear);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('arc.nav.collapsed') === '1',
  );
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const scope = session?.scope.level ?? 'tenant';

  const toggle = () => {
    setCollapsed((v) => {
      // Per-viewer convenience only. Wrapped because private-mode browsers
      // throw on write, and a nav toggle must never break the app.
      try { localStorage.setItem('arc.nav.collapsed', v ? '0' : '1'); } catch { /* ignore */ }
      return !v;
    });
  };

  const visible = (i: NavItem): boolean => {
    if (i.permission && !can(i.permission)) return false;
    if (i.scopes && !i.scopes.includes(scope)) return false;
    return true;
  };

  const initials = (session?.user.full_name ?? session?.user.email ?? '?')
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('');

  return (
    <div className="arc-app">
      <aside className="arc-sidebar" data-collapsed={collapsed}>
        <div className="arc-brand">
          <div className="arc-brand-mark" aria-hidden="true">A</div>
          {!collapsed && <span>ArcNet</span>}
        </div>

        <nav className="arc-nav" aria-label="Main">
          {NAV.map((section) => {
            const items = section.items.filter(visible);
            if (items.length === 0) return null;
            return (
              <div key={section.title}>
                {!collapsed && <div className="arc-nav-section">{section.title}</div>}
                {items.map((item) => {
                  const active = item.to === '/'
                    ? pathname === '/'
                    : pathname.startsWith(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="arc-nav-item"
                      data-active={active}
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="arc-nav-icon" aria-hidden="true">{item.icon}</span>
                      {!collapsed && <span>{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="arc-sidebar-foot">
          <button
            type="button"
            className="arc-nav-item"
            onClick={toggle}
            style={{ width: '100%', border: 'none', background: 'none', font: 'inherit' }}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            <span className="arc-nav-icon" aria-hidden="true">{collapsed ? '»' : '«'}</span>
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      <div className="arc-main">
        <header className="arc-header">
          <ScopeIndicator />
          <TenantSwitcher />
          <div className="arc-spacer" />

          {session?.impersonated_by && (
            <div className="arc-badge arc-badge-warn" role="status">
              Impersonating — actions are audited
            </div>
          )}

          <ThemeToggle />

          <div className="arc-row">
            <div className="arc-avatar" aria-hidden="true">{initials}</div>
            <div className="arc-stack" style={{ lineHeight: 1.25 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {session?.user.full_name ?? session?.user.email}
              </span>
              <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                {session?.memberships[0]?.role_name ?? '—'}
              </span>
            </div>
            <button
              type="button"
              className="arc-btn arc-btn-ghost arc-btn-sm"
              onClick={() => { void logout().then(clear); }}
            >
              Sign out
            </button>
          </div>
        </header>

        <main className="arc-content">
          <div className="arc-page-max">{children}</div>
        </main>
      </div>
    </div>
  );
}

function ScopeIndicator() {
  const session = useSession((s) => s.session);
  if (!session) return null;
  const label =
    session.scope.level === 'platform' ? 'Platform'
    : session.scope.level === 'reseller' ? 'Reseller'
    : session.memberships[0]?.tenant_name ?? 'Tenant';
  return (
    <div className="arc-row" style={{ gap: 8 }}>
      <span className="arc-badge arc-badge-info">
        <span className="arc-dot" aria-hidden="true" />
        {label}
      </span>
      {session.scope.level === 'platform' && (
        <span className="arc-subtle" style={{ fontSize: 12.5 }}>
          all tenants
        </span>
      )}
    </div>
  );
}

function ThemeToggle() {
  const [theme, setTheme] = useState<string>(
    () => document.documentElement.dataset.theme ?? 'system',
  );
  const cycle = () => {
    const next = theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system';
    setTheme(next);
    if (next === 'system') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = next;
    try { localStorage.setItem('arc.theme', next); } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      className="arc-btn arc-btn-ghost arc-btn-icon"
      onClick={cycle}
      title={`Theme: ${theme}`}
      aria-label={`Theme: ${theme}. Click to change.`}
    >
      {theme === 'dark' ? '☾' : theme === 'light' ? '☀' : '◐'}
    </button>
  );
}
